import styled from 'styled-components';

import { AnchorButton } from 'src/components/core/button';
import { EmbeddedAccountIcon } from 'src/assets/embedded-account-icon';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

export const ConsumerInfoContainer = styled.div<{ $isUserLoggedIn: boolean }>`
  position: fixed;
  z-index: 99;
  bottom: 160px;
  left: 20px;
  right: 20px;
  border-radius: 24px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 12px 12px rgb(0 0 0 / 10%);
  max-width: 1000px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  text-align: center;
  font-size: clamp(16px, 1.5vw, 20px);
  font-weight: 700;

  @media (orientation: landscape) {
    right: auto;
    bottom: 0;
    border-radius: 24px 24px 0 0;
    flex-direction: ${({ $isUserLoggedIn }) => ($isUserLoggedIn ? 'column' : 'column-reverse')};
    align-items: flex-start;
    width: 100%;
    max-width: 348px;
    left: 26px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    left: 20px;
    max-width: 280px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    max-width: 248px;
    left: 16px;
  }

  @media (orientation: portrait) {
    max-height: 146px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    bottom: 112px;
    max-height: 124px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    bottom: 100px;
    max-height: 102px;
  }
`;

export const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 30px 30px 0 40px;
  font-size: clamp(20px, 1.5vw, 24px);

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    padding: 20px 20px 0 24px;
    font-size: clamp(16px, 1.5vw, 20px);
  }

  @media (orientation: portrait) {
    padding: 44px 24px;
    min-width: 0;
    overflow: hidden;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding: 26px 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    padding: 24px 16px;
  }
`;

export const ThemedEmbeddedAccountIcon = styled(EmbeddedAccountIcon)`
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  width: 36px;
  height: 36px;

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth}, ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    width: 28px;
    height: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    width: 24px;
    height: 24px;
  }
`;

export const InfoText = styled.div`
  text-align: left;
  color: ${({ theme }) => theme.colors.grey[40]};
  line-height: 1.4;
  font-size: 24px;
  margin-left: 16px;

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth}, ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 16px;
    margin-left: 14px;
  }
`;

export const Points = styled.p`
  color: ${({ theme }) => theme.colors.black};
`;

export const ButtonContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 30px;

  @media (orientation: landscape) {
    flex-direction: column;
    width: 100%;
    padding: 24px;
    gap: 16px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    padding: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    padding: 16px;
    gap: 14px;
  }
`;

// forwardedAs (not `as`): `as` would replace AnchorButton entirely and drop buttonStyles.
// type='button' prevents implicit submit. styled-normalize's `[type=button]` appearance reset wins
// the specificity tie over `appearance: none` here; that's inert because the explicit border/
// background below suppress native chrome regardless (see PR for the computed-style evidence).
export const BaseButton = styled(AnchorButton).attrs({ forwardedAs: 'button', type: 'button' })`
  width: 100%;
  padding: 24px 31px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  border: 4px solid ${({ theme }) => theme.customized.colors.buttonsLinks};
  /* <button> gets its text-align from the UA sheet rather than inheriting it from
     ConsumerInfoContainer the way the <a> being replaced did, so set it explicitly. */
  text-align: center;

  @media (orientation: portrait) {
    min-width: max-content;
  }

  @media (orientation: landscape) {
    padding: 24px 36px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    padding: 20px 28px;
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    padding: 16px 24px;
    font-size: 16px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;
    padding: 20px 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    font-size: 16px;
    padding: 16px 24px;
  }
`;

// No own rules -- mirrors EndSessionButton's pattern of a named wrapper per button
// so CART and END SESSION are equally easy to target/extend independently of BaseButton.
export const CartButton = styled(BaseButton)``;

export const EndSessionButton = styled(BaseButton)`
  width: 200px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};

  @media (orientation: landscape) {
    width: 100%;
  }
`;

// span, not div: this renders inside LoginButton, and <button> only permits phrasing
// content descendants. display: flex (below) keeps the layout identical to the div it replaces.
export const Badge = styled.span`
  background-color: #26a27b;
  color: #fff;
  border-radius: 40px;
  width: fit-content;
  margin: 0 auto 10px auto;
  display: flex;
  align-items: center;
  font-weight: 700;

  & svg {
    margin-right: 4px;
  }

  @media (orientation: landscape) {
    font-size: 14px;
    padding: 4px 8px;

    & svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (orientation: portrait) {
    font-size: 18px;
    padding: 4px 12px;

    & svg {
      width: 24px;
      height: 24px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 10px;
    padding: 4px 6px;

    & svg {
      width: 14px;
      height: 14px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 16px;
    padding: 4px 12px;

    & svg {
      width: 20px;
      height: 20px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    font-size: 14px;
    padding: 4px 12px;

    & svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// span, not div: this renders inside LoginButton, and <button> only permits phrasing
// content descendants. display: block reproduces the div's default block layout explicitly.
export const LoginContainer = styled.span`
  display: block;
  width: 100%;
  padding: 24px;
  max-height: 146px;

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth}, ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    padding: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    padding: 16px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    max-height: 124px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    padding: 16px;
    max-height: 102px;
  }
`;

export const LoginButton = styled(AnchorButton).attrs({ forwardedAs: 'button', type: 'button' })`
  width: 100%;
  background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  color: ${({ theme }) => theme.colors.white};
  padding: 0;
  border-radius: 0;
  text-align: center;
  font-size: 24px;
  line-height: 1.2;
  position: relative;
  overflow: hidden;

  z-index: 1;

  &::before {
    content: '';
    height: 400px;
    width: 400px;
    left: -20px;
    top: 40px;
    pointer-events: none;
    position: absolute;
    z-index: -1;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
  }

  @media (orientation: portrait) {
    border-radius: 24px 0 0 24px;
    max-width: 288px;

    &::before {
      left: -50px;
      top: 40px;
    }
  }

  &:hover {
    background-image: none;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 18px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;
    max-width: 232px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    font-size: 16px;
    max-width: 188px;
  }
`;

// span, not p: this renders inside LoginButton, and <button> only permits phrasing
// content descendants. display: block reproduces the p's default block layout explicitly.
export const LoginText = styled.span`
  display: block;
  text-transform: none;
  font-size: 24px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 16px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    font-size: 16px;
  }
`;
